import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail } from 'lucide-react';
import { NotionSelect, type NotionSelectOption } from './NotionSelect';
import Drawer from '../ui/Drawer';
import { Eyebrow, Field, Mono, Pill } from '../ui/kit';
import type { Lead } from '../../types';

interface LeadDetailModalProps {
  lead: Lead | null;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (updatedLead: Lead) => void;
}

/**
 * A lead has exactly one urgent job attached to it: call the person back. So
 * the phone number and the words they actually typed come first, and the CRM
 * fields — stage, source, timestamps — sit under them.
 *
 * `service`/`message`/`urgency` now live in their own columns (see backend
 * `routes/portalIntake.ts`) and `notes` is purely the owner's own free-text —
 * new leads never mix the two.
 *
 * Older leads (created before that split) still have the submission and the
 * owner's own notes crammed into one `notes` column, with the public intake
 * route's old `Form:/Service:/Message:/Urgency:` lines followed by a `---`
 * separator and whatever the owner typed. `parseNotes`/`composeNotes` below
 * are kept as the read/write path for exactly those legacy rows — every
 * current lead skips them entirely and reads/writes the structured columns
 * and a plain `notes` string.
 */

/** Tones match `statusConfig` in LeadsPage, so the chip here reads like the list's. */
const STAGE_OPTIONS: NotionSelectOption[] = [
  { value: 'new', label: 'New', tone: 'live' },
  { value: 'contacted', label: 'Contacted', tone: 'neutral' },
  { value: 'qualified', label: 'Qualified', tone: 'violet' },
  { value: 'converted', label: 'Converted', tone: 'live' },
  { value: 'lost', label: 'Lost', tone: 'danger' },
];

const SOURCE_OPTIONS: NotionSelectOption[] = [
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'social', label: 'Social Media' },
  { value: 'manual', label: 'Added by hand' },
  { value: 'other', label: 'Other' },
];

/** Separates the intake submission from the owner's own notes. */
const NOTES_SEPARATOR = '---';

/** Keys the public intake route writes, in the order it writes them. */
const SUBMISSION_KEYS = ['Form', 'Service', 'Message', 'Urgency'];

interface ParsedNotes {
  /** Field/value pairs the visitor submitted. Empty for a manually added lead. */
  submission: { label: string; value: string }[];
  /** Anything that isn't a recognised submission line — the owner's own note. */
  own: string;
}

function parseNotes(notes: string | null | undefined): ParsedNotes {
  if (!notes) return { submission: [], own: '' };

  const separatorIndex = notes.split('\n').findIndex(line => line.trim() === NOTES_SEPARATOR);
  const lines = notes.split('\n');
  const submissionText = separatorIndex >= 0 ? lines.slice(0, separatorIndex).join('\n') : notes;
  const own = separatorIndex >= 0 ? lines.slice(separatorIndex + 1).join('\n') : '';

  // A submission is a run of `Key: value` lines. `Message:` is the one that can
  // run over several lines, so anything that isn't a new key belongs to it.
  const submission: { label: string; value: string }[] = [];
  for (const line of submissionText.split('\n')) {
    const match = line.match(/^([A-Za-z ]+):\s?([\s\S]*)$/);
    if (match && SUBMISSION_KEYS.includes(match[1])) {
      submission.push({ label: match[1], value: match[2] });
    } else if (submission.length > 0) {
      const last = submission[submission.length - 1];
      last.value = `${last.value}\n${line}`;
    }
  }

  // No recognised keys at all — a lead someone typed in by hand. All of it is
  // the owner's note, and nothing is claimed as the customer's words.
  if (submission.length === 0) {
    return { submission: [], own: separatorIndex >= 0 ? own : submissionText };
  }

  return { submission, own: own.trim() };
}

function composeNotes(submissionText: string, own: string): string | null {
  const trimmedOwn = own.trim();
  if (!submissionText) return trimmedOwn || null;
  if (!trimmedOwn) return submissionText;
  return `${submissionText}\n${NOTES_SEPARATOR}\n${trimmedOwn}`;
}

/** The submission half of `notes`, verbatim, so a save round-trips it intact. */
function submissionSource(notes: string | null | undefined): string {
  if (!notes) return '';
  const lines = notes.split('\n');
  const separatorIndex = lines.findIndex(line => line.trim() === NOTES_SEPARATOR);
  const text = separatorIndex >= 0 ? lines.slice(0, separatorIndex).join('\n') : notes;
  const hasSubmission = text
    .split('\n')
    .some(line => SUBMISSION_KEYS.some(key => line.startsWith(`${key}:`)));
  return hasSubmission ? text : '';
}

/**
 * True only for a legacy lead whose `notes` still carries the old blob format.
 *
 * A blob is legacy only when:
 * 1. notes contain a line starting with 'Form:' (which only the old intake route wrote)
 * 2. AND the lead's structured columns (service/message/urgency) are ALL empty
 *
 * This prevents false positives from manually-typed owner notes like "Service: needs follow-up call".
 */
function isLegacyNotesBlob(notes: string | null | undefined, service?: string | null, message?: string | null, urgency?: string | null): boolean {
  if (!notes) return false;

  // Check if any structured column is populated — if so, it's a modern lead, not legacy
  if (service || message || urgency) return false;

  // Check for the 'Form:' line, which only the old intake route wrote
  const hasFormLine = notes.split('\n').some(line => line.startsWith('Form:'));
  return hasFormLine;
}

/**
 * What the visitor submitted, for display. Reads the structured columns for
 * every current lead; falls back to parsing the old `notes` blob for legacy
 * rows that never had those columns populated.
 */
function getSubmission(lead: Lead | null | undefined): { label: string; value: string }[] {
  if (!lead) return [];
  if (isLegacyNotesBlob(lead.notes, lead.service, lead.message, lead.urgency)) {
    return parseNotes(lead.notes).submission;
  }
  const submission: { label: string; value: string }[] = [];
  if (lead.service) submission.push({ label: 'Service', value: lead.service });
  if (lead.message) submission.push({ label: 'Message', value: lead.message });
  if (lead.urgency && lead.urgency !== 'normal') submission.push({ label: 'Urgency', value: lead.urgency });
  return submission;
}

/** The owner's own note, unmixed with any submission text. */
function getOwnNotes(lead: Lead | null | undefined): string {
  if (!lead) return '';
  if (isLegacyNotesBlob(lead.notes, lead.service, lead.message, lead.urgency)) {
    return parseNotes(lead.notes).own;
  }
  return lead.notes ?? '';
}

/** Builds the `notes` value to save, preserving the legacy blob shape only when the lead already has one. */
function composeNotesForSave(lead: Lead | null | undefined, ownNotes: string): string | null {
  if (lead && isLegacyNotesBlob(lead.notes, lead.service, lead.message, lead.urgency)) {
    return composeNotes(submissionSource(lead.notes), ownNotes);
  }
  return ownNotes.trim() || null;
}

interface Draft {
  name: string;
  status: string;
  source: string;
  email: string;
  phone: string;
  /** Only the owner's half of `notes`. The submission half is never edited. */
  ownNotes: string;
}

function toDraft(lead: Lead | null): Draft {
  return {
    name: lead?.name ?? '',
    status: lead?.status ?? 'new',
    source: lead?.source ?? 'website',
    email: lead?.email ?? '',
    phone: lead?.phone ?? '',
    ownNotes: getOwnNotes(lead),
  };
}

const LeadDetailModal: React.FC<LeadDetailModalProps> = ({ lead, isOpen, onClose, onSave }) => {
  const notesTextareaRef = useRef<HTMLTextAreaElement>(null);

  const [draft, setDraft] = useState<Draft>(() => toDraft(lead));
  const submission = getSubmission(lead);

  /*
    Re-fill the draft only when a *different* lead is shown.

    This used to be an effect keyed on the whole `lead` object that pushed each
    field into its own state, paired with a second effect that diffed that state
    against the prop and called `onSave` on any difference. Both ran in the same
    commit, and effects see the state of the render they were queued from — so
    the save effect compared the *incoming* lead against the *outgoing* empty
    draft, decided everything had "changed", and wrote `name: ''`, `email: null`,
    `phone: null`, `notes: null` straight over the record the moment you opened
    it. Saving now happens only in `commit`, from a real user edit, where the
    draft it reads is the one on screen.
  */
  const hydratedIdRef = useRef<string | undefined>(lead?.id);
  useEffect(() => {
    if (lead && lead.id !== hydratedIdRef.current) {
      hydratedIdRef.current = lead.id;
      setDraft(toDraft(lead));
    }
  }, [lead]);

  // Auto-resize the note field to fit what's in it.
  useEffect(() => {
    const textarea = notesTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [draft.ownNotes]);

  /** Apply one field edit and hand the whole record up. The caller debounces. */
  const commit = (patch: Partial<Draft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (!lead || !onSave) return;
    onSave({
      ...lead,
      name: next.name,
      status: next.status,
      source: next.source,
      email: next.email || null,
      phone: next.phone || null,
      notes: composeNotesForSave(lead, next.ownNotes),
    });
  };

  if (!lead) return null;

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Lead"
      title={draft.name || 'Unnamed lead'}
      subtitle={`${lead.source} · ${formatDate(lead.createdAt)}`}
      width="xl"
      footer={
        // The whole point of the screen. Neither is destructive, so both stay
        // one tap away instead of behind a menu.
        <div className="flex flex-wrap items-center gap-2">
          {draft.phone && (
            <Pill href={`tel:${draft.phone}`} className="flex-1 sm:flex-none">
              <Phone size={15} />
              Call {draft.phone}
            </Pill>
          )}
          {draft.email && (
            <Pill
              href={`mailto:${draft.email}`}
              tone={draft.phone ? 'outline' : 'solid'}
              className="flex-1 sm:flex-none"
            >
              <Mail size={15} />
              Email
            </Pill>
          )}
          {!draft.phone && !draft.email && (
            <p className="text-[13px] text-muted">
              No phone or email on this lead — add one below to get in touch.
            </p>
          )}
        </div>
      }
    >
      <div className="space-y-8">
        {/*
          What they actually sent, in their words. Read-only: this is a record
          of an event, not a field, and editing it would quietly rewrite
          history on the one thing you might have to act on in a dispute.
        */}
        {submission.length > 0 && (
          <section>
            <Eyebrow className="mb-3">What they sent</Eyebrow>
            <div className="rounded-xl border border-zinc-200 bg-paper-tint">
              <dl className="divide-y divide-zinc-200">
                {submission.map(entry => (
                  <div key={entry.label} className="flex gap-4 px-4 py-3">
                    <dt className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                      {entry.label}
                    </dt>
                    <dd className="min-w-0 flex-1 whitespace-pre-wrap text-[14px] leading-relaxed text-primary">
                      {entry.value.trim() || '—'}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        )}

        {/* Photos the visitor attached to the form — thumbnails open the full image in a new tab. */}
        {lead.photoUrls && lead.photoUrls.length > 0 && (
          <section>
            <Eyebrow className="mb-3">Photos</Eyebrow>
            <div className="flex flex-wrap gap-3">
              {lead.photoUrls.map((url, i) => (
                <a
                  key={url + i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block h-24 w-24 overflow-hidden rounded-lg border border-zinc-200 transition hover:ring-2 hover:ring-accent"
                >
                  <img src={url} alt={`Lead photo ${i + 1}`} className="h-full w-full object-cover" />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Contact — editable, because a typo'd number is worth fixing. */}
        <section className="space-y-5">
          <Eyebrow>Contact</Eyebrow>
          <Field label="Name" htmlFor="lead-name">
            <input
              id="lead-name"
              value={draft.name}
              onChange={e => commit({ name: e.target.value })}
              maxLength={512}
              placeholder="Lead name"
              className="input-field"
            />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Phone" htmlFor="lead-phone">
              <input
                id="lead-phone"
                type="tel"
                value={draft.phone}
                onChange={e => commit({ phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="input-field"
              />
            </Field>
            <Field label="Email" htmlFor="lead-email">
              <input
                id="lead-email"
                type="email"
                value={draft.email}
                onChange={e => commit({ email: e.target.value })}
                placeholder="email@example.com"
                className="input-field"
              />
            </Field>
          </div>
        </section>

        {/*
          Where it is in your pipeline.

          Same two-column `Field` stack as Contact above. It used to be a
          label-on-the-left row holding a borderless button, so the panel ran
          one form layout for the text inputs and a second one for the selects
          — and the stage chip came out sentence-case blue against the
          monospace green `NEW` the list shows for the same lead.
        */}
        <section className="space-y-5">
          <Eyebrow>Pipeline</Eyebrow>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Stage">
              <NotionSelect
                value={draft.status}
                onChange={value => commit({ status: value as string })}
                options={STAGE_OPTIONS}
                showAsTag
              />
            </Field>
            <Field label="Source">
              <NotionSelect
                value={draft.source}
                onChange={value => commit({ source: value as string })}
                options={SOURCE_OPTIONS}
              />
            </Field>
          </div>
        </section>

        {/* Your own note, kept apart from theirs. */}
        <section>
          <Eyebrow className="mb-3">Your notes</Eyebrow>
          <textarea
            ref={notesTextareaRef}
            value={draft.ownNotes}
            onChange={e => commit({ ownNotes: e.target.value })}
            placeholder="What you agreed, what to follow up on…"
            className="input-field resize-none overflow-hidden leading-relaxed"
            style={{ minHeight: '5rem' }}
          />
          {submission.length > 0 && (
            <p className="mt-2 text-[13px] text-muted">
              Kept separate from the form submission above.
            </p>
          )}
        </section>

        <section className="border-t border-zinc-200 pt-5">
          <dl className="space-y-2">
            <div className="flex gap-4">
              <dt className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                Received
              </dt>
              <dd>
                <Mono>{formatDate(lead.createdAt)}</Mono>
              </dd>
            </div>
            <div className="flex gap-4">
              <dt className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                Updated
              </dt>
              <dd>
                <Mono>{formatDate(lead.updatedAt)}</Mono>
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </Drawer>
  );
};

export default LeadDetailModal;
